import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environment";
import { Observable } from "rxjs";
import { DropdownItem } from "src/app/common/interfaces/dropdown-item.interface";
import {
  SecurityGroup,
  SecurityGroupItem,
} from "src/app/features/security-group/interfaces/security-group.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import { sanitizeParams } from "../sanitize-params";

@Injectable({ providedIn: "root" })
export class SecurityGroupClient {
  endpoint = "security-group";
  constructor(private httpClient: HttpClient) {}

  getSecurityGroupsDropdown(
    corpId: string,
    includeDefault: boolean = false,
    userId?: string
  ): Observable<DropdownItem[]> {
    const params = new URLSearchParams();

    if (includeDefault) {
      params.append("includeDefault", String(includeDefault));
    }

    if (userId) {
      params.append("userId", userId);
    }

    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/dropdown${
        corpId ? `/${corpId}` : ""
      }${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  getSecurityGroup(securityGroupId: string) {
    return this.httpClient.get<SecurityGroup>(
      `${environment.endpoint}/${this.endpoint}/get/${securityGroupId}`
    );
  }

  getSecurityGroups(filters: {
    corpId?: string;
    keyword?: string;
    page?: number;
    length?: number;
    sortBy?: string;
    sortDir?: string;
  }): Observable<ListResponse<SecurityGroupItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<SecurityGroupItem>>(
      `${environment.endpoint}/${this.endpoint}/list`,
      { params }
    );
  }

  deleteWithReassign(data: {
    securitygroupid: string;
    assignedgroups: { userid: string; securitygroupid: string }[];
  }) {
    return this.httpClient.put<SecurityGroup>(
      `${environment.endpoint}/${this.endpoint}/delete-with-re-assign`,
      data
    );
  }

  deleteSecurityGroup(securityGroupId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete/${securityGroupId}`
    );
  }

  enableSecurityGroup(securityGroupId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/enabled/${securityGroupId}`
    );
  }

  disableSecurityGroup(securityGroupId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/disabled/${securityGroupId}`
    );
  }

  addEditSecurityGroup(
    securityGroup: SecurityGroup
  ): Observable<SecurityGroup> {
    if (securityGroup.id) {
      return this.httpClient.put<SecurityGroup>(
        `${environment.endpoint}/${this.endpoint}/edit`,
        securityGroup
      );
    } else {
      return this.httpClient.post<SecurityGroup>(
        `${environment.endpoint}/${this.endpoint}/add`,
        securityGroup
      );
    }
  }

  getFeaturePermission(facilityId?: string) {
    if (facilityId) {
      return this.httpClient.get(
        `${environment.endpoint}/${this.endpoint}/get-feature-permission-flat?facilityid=${facilityId}`
      );
    }

    return this.httpClient.get(
      `${environment.endpoint}/${this.endpoint}/get-feature-permission-flat`
    );
  }
}
